import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";

import type { AuthenticatedUser } from "@shared/schemas/entities";

import { apiGet } from "../api/client";
import { adoptCacheIdentity, type CacheOwner } from "../query/cacheIdentity";
import { supabase } from "./supabaseClient";
import { clearImpersonate, writeImpersonate } from "./impersonateStore";
import {
  clearLastKnownUser,
  readLastKnownUser,
  writeLastKnownUser,
} from "./lastKnownUser";
import { isAdminRole, isSuperadminRole } from "./roles";

export {
  isAdminRole,
  isSuperadminRole,
  isPLRole,
  isBuchhaltungRole,
  isFinanceRole,
  isProjectLead,
} from "./roles";

export type { AuthenticatedUser };

type AuthStatus = "loading" | "signed-out" | "signed-in";

export interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  user: AuthenticatedUser | null;
  /** Wenn gesetzt: wir handeln als jemand anderes. Der Wert ist die Ziel-User-ID. */
  actedAsUserId: string | null;
  signInWithMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
  startImpersonate: (userId: string) => Promise<void>;
  stopImpersonate: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface MeResponse {
  ok: true;
  data: {
    user: AuthenticatedUser;
    actedAsUserId: string | null;
  };
}

async function fetchMe(): Promise<MeResponse["data"]> {
  const res = await apiGet<MeResponse>("/api/me");
  return res.data;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Session 16: optimistischer Initial-State. Wenn ein Last-Known-User
  // im localStorage liegt, starten wir mit "signed-in" + cached User. fetchMe()
  // laeuft im Hintergrund. Ohne Cache: regulaerer "loading"-Pfad.
  const cachedUser = readLastKnownUser();
  // STABIL-4: der Abfrage-Cache haengt an genau einer Person. Jeder Wechsel
  // der Identitaet (Vertretung an/aus, An-/Abmeldung) laeuft durch
  // `adoptCacheIdentity` — sonst zeigt die Startseite die Karten des
  // Vorgaengers weiter (Daniel sah seine IT-Alarme unter Marvins Namen).
  const queryClient = useQueryClient();

  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AuthenticatedUser | null>(cachedUser);
  const [actedAsUserId, setActedAsUserId] = useState<string | null>(null);
  const [status, setStatus] = useState<AuthStatus>(
    cachedUser ? "signed-in" : "loading",
  );
  // Session 16b: verhindert, dass der session-useEffect bei initialem `null`
  // sofort auf "signed-out" zieht. Ohne dieses Flag trat bei Direkt-URL
  // (z.B. /system/selftests) folgender Race auf: 1) session=null (async
  // getSession pending), 2) useEffect feuert mit !session → signed-out,
  // RequireAuth navigiert zu /login, 3) getSession resolvt Session, useEffect
  // feuert wieder → fetchMe → signed-in, 4) zu spaet: Browser ist auf /login,
  // spaeter auf /. Die Superadmin-Route ist weg.
  const [initialSessionChecked, setInitialSessionChecked] = useState(false);

  // STABIL-12: Der Zaehler der Identitaets-Uebergaenge. Er haengt als `key`
  // an allem, was unter diesem Provider liegt — steigt er, baut React den
  // ganzen Baum neu auf. Warum das noetig ist, steht bei `adoptIdentity`.
  const [identityEpoch, setIdentityEpoch] = useState(0);

  // Der EINE Ort, an dem die Identitaet des Caches wechselt.
  //
  // STABIL-4 hat Speicher und Spiegel geraeumt. Das reichte nicht, und der
  // Live-Befund vom 29.07.2026 zeigt warum: `queryClient.clear()` leert den
  // Cache, benachrichtigt aber keinen einzigen montierten Beobachter. Ein
  // `useQuery`, das gerade auf dem Bildschirm steht, haelt sein letztes
  // Ergebnis in der eigenen Instanz — mit `status: "success"`. Es fragt
  // nichts nach (gemessen: nach dem Wechsel ging KEIN `/api/notifications`
  // mehr raus) und rendert den alten Stand unveraendert weiter. Ob der
  // Fehler auffiel, entschied allein der Zufall: kam die Anzeige durch den
  // Rollenwechsel ohnehin neu auf die Welt, heilte sie; blieb sie stehen,
  // sah man die Karten der vorigen Person. Daniel sah Sandros Startseite mit
  // seinem eigenen IT-Alarm und 122 fehlenden Meldungen.
  //
  // Deshalb faellt beim Wechsel jetzt auch der dritte Traeger weg: die
  // Beobachter. Ein neuer `key` wirft sie alle weg, samt dem, was sie sich
  // gemerkt haben (auch `placeholderData: keepPreviousData` kann so nichts
  // aus dem vorigen Leben nachreichen). Die neuen holen sich ihre Daten mit
  // dem neuen Vertretungs-Kopf.
  //
  // Bleibt die Person gleich — der normale Neulade-Fall — passiert weiterhin
  // nichts: kein Raeumen, kein Neuaufbau, der Persist-Gewinn bleibt heil.
  // VTR-7: uebergeben wird das PAAR, nicht die angezeigte Person. «Marvin ist
  // angemeldet» und «Daniel handelt als Marvin» ergaben vorher dieselbe
  // Kennung — und ein Spiegel aus dem einen Zustand galt damit im anderen.
  const adoptIdentity = useCallback(
    (owner: CacheOwner | null) => {
      if (adoptCacheIdentity(queryClient, owner)) {
        setIdentityEpoch((n) => n + 1);
      }
    },
    [queryClient],
  );

  /**
   * Das Paar, wie es aus einer `/api/me`-Antwort folgt.
   *
   * Der Server nennt in `user` den WIRKSAMEN Menschen und in `actedAsUserId`
   * den echten Anmelder — aber nur waehrend einer Vertretung (Konvention
   * 5.72-B-1.5). Ohne Vertretung ist `user` selbst der Anmelder.
   */
  const ownerAusMe = useCallback(
    (data: MeResponse["data"]): CacheOwner => ({
      anmelderId: data.actedAsUserId ?? data.user.id,
      vertretenerId: data.actedAsUserId === null ? null : data.user.id,
    }),
    [],
  );

  // Session-Stream von Supabase.
  useEffect(() => {
    let cancelled = false;

    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setInitialSessionChecked(true);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setInitialSessionChecked(true);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // FIN-3 (2026-07-29) — «wenn Session wechselt» hiess im Code «wenn das
  // Session-OBJEKT wechselt».
  //
  // Daniel hat beim Rundgang gemessen: beim Laden der Admin-Startseite ging
  // GET /api/me ueber zwoelfmal raus. Der Grund steckt in der Abhaengigkeit:
  // `setSession(next)` legt bei JEDEM Supabase-Ereignis ein neues Objekt ab,
  // und Supabase meldet sich mehrfach, ohne dass sich etwas geaendert haette —
  // die aufgeloeste `getSession()`-Zusage, `INITIAL_SESSION`, spaeter
  // `TOKEN_REFRESHED`, und bei manchen Browsern auch beim Zurueckkehren auf den
  // Tab. Jedes dieser Ereignisse war eine neue Objekt-Identitaet, also ein
  // neuer Effekt-Lauf, also ein neues `/api/me`.
  //
  // Der Effekt haengt jetzt am ZUGRIFFS-TOKEN. Ein Ereignis, das dieselbe
  // Anmeldung nochmal meldet, laeuft ins Leere; ein echter Token-Wechsel
  // (Anmeldung, Abmeldung, Erneuerung) fragt weiterhin nach.
  //
  // EHRLICH GESAGT: die genaue Zahl zwoelf habe ich nicht reproduzieren
  // koennen — sie stammt aus einem Live-Mitschnitt. Belegt ist der Mechanismus
  // (neue Objekt-Identitaet pro Ereignis), und der ist hiermit weg.
  const accessToken = session?.access_token ?? null;

  // Wenn die Anmeldung wechselt: User-Row aus unserer DB laden (mit moeglichem
  // Impersonate).
  useEffect(() => {
    // Session 16b: warten, bis die erste getSession-Antwort da ist. Sonst
    // kollidiert der optimistische cached-user-Start mit dem initialen
    // session=null.
    if (!initialSessionChecked) return;

    if (!accessToken) {
      adoptIdentity(null);
      setUser(null);
      setActedAsUserId(null);
      setStatus("signed-out");
      clearLastKnownUser();
      return;
    }

    let cancelled = false;
    // Wenn wir bereits optimistisch "signed-in" sind, KEIN Status-Reset auf
    // "loading" — das wuerde wieder den Flash erzeugen. fetchMe laeuft still
    // im Hintergrund und aktualisiert User wenn noetig.
    setStatus((prev) => (prev === "signed-in" ? prev : "loading"));
    fetchMe()
      .then((data) => {
        if (cancelled) return;
        // Die massgebliche Antwort auf «wer bin ich gerade». Deckt auch den
        // Fall ab, den kein Knopfdruck ausloest: ein zweiter Tab erbt den
        // gespiegelten Cache aus localStorage, obwohl dort niemand vertreten
        // wird. Stimmt die Kennung ueberein, ist das ein reiner No-Op.
        adoptIdentity(ownerAusMe(data));
        setUser(data.user);
        setActedAsUserId(data.actedAsUserId);
        setStatus("signed-in");
        writeLastKnownUser(data.user);
      })
      .catch(() => {
        if (cancelled) return;
        // User hat gueltigen Supabase-JWT, existiert aber nicht in unserer
        // users-Tabelle (oder deaktiviert). Session beenden.
        void supabase.auth.signOut();
        adoptIdentity(null);
        setUser(null);
        setActedAsUserId(null);
        setStatus("signed-out");
        clearLastKnownUser();
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, initialSessionChecked, adoptIdentity, ownerAusMe]);

  // Wer gerade angemeldet IST — unabhaengig davon, wer angezeigt wird. Bei
  // einer Vertretung ist das der Vertreter (`actedAsUserId`), sonst der
  // angezeigte Mensch selbst. Die Haelfte des Paares, die ein Wechsel der
  // Vertretung NICHT veraendert.
  const anmelderId = actedAsUserId ?? user?.id ?? null;

  const value = useMemo<AuthContextValue>(() => {
    return {
      status,
      session,
      user,
      actedAsUserId,
      signInWithMicrosoft: async () => {
        const redirectTo = `${window.location.origin}/auth/callback`;
        const { error } = await supabase.auth.signInWithOAuth({
          provider: "azure",
          options: {
            scopes: "openid email profile",
            redirectTo,
          },
        });
        if (error) throw new Error(error.message);
      },
      signOut: async () => {
        clearImpersonate();
        clearLastKnownUser();
        // Vor dem Abmelden raeumen: sonst erbt der naechste Mensch an diesem
        // Geraet den gespiegelten Cache des vorigen.
        adoptIdentity(null);
        await supabase.auth.signOut();
      },
      startImpersonate: async (userId: string) => {
        writeImpersonate(userId);
        // Schon VOR dem Nachfragen raeumen. Wir wissen hier bereits, wessen
        // Karten ab jetzt gelten — wartet man auf die Antwort, rendert die
        // Startseite in der Zwischenzeit weiter die Karten des Anmelders.
        // Genau das war Daniels Befund.
        //
        // Der Anmelder bleibt derselbe Mensch: vertritt er schon jemanden,
        // steht er in `actedAsUserId`, sonst ist er selbst der angezeigte.
        adoptIdentity(
          anmelderId === null
            ? null
            : { anmelderId, vertretenerId: userId },
        );
        const data = await fetchMe();
        adoptIdentity(ownerAusMe(data));
        setUser(data.user);
        setActedAsUserId(data.actedAsUserId);
        writeLastKnownUser(data.user);
      },
      stopImpersonate: async () => {
        clearImpersonate();
        // Zurueck zum echten Anmelder — der steht waehrend der Vertretung in
        // `actedAsUserId`. Ohne dieses Raeumen blieben die Karten des
        // Vertretenen unter dem eigenen Namen stehen.
        adoptIdentity(
          anmelderId === null ? null : { anmelderId, vertretenerId: null },
        );
        const data = await fetchMe();
        adoptIdentity(ownerAusMe(data));
        setUser(data.user);
        setActedAsUserId(data.actedAsUserId);
        writeLastKnownUser(data.user);
      },
    };
  }, [status, session, user, actedAsUserId, anmelderId, adoptIdentity, ownerAusMe]);

  return (
    <AuthContext.Provider value={value}>
      {/*
        STABIL-12 — der `key` ist die Reissleine. Steigt `identityEpoch`,
        verwirft React alles darunter und baut es neu auf. Damit stirbt jeder
        Beobachter, der sich noch an die vorige Person erinnert; die Alternative
        waere, jede einzelne Abfrage im Haus mit einer Kennung zu versehen —
        und die eine, die es kuenftig vergisst, faellt niemandem auf. Genau so
        ist dieser Fehler entstanden.
      */}
      <Fragment key={identityEpoch}>{children}</Fragment>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth muss innerhalb <AuthProvider> genutzt werden.");
  return ctx;
}

export function useRequiredUser(): AuthenticatedUser {
  const { user } = useAuth();
  if (!user) throw new Error("useRequiredUser: kein User im Context.");
  return user;
}

export function useIsAdmin(): boolean {
  const { user } = useAuth();
  return user ? isAdminRole(user.role) : false;
}

export function useIsSuperadmin(): boolean {
  const { user } = useAuth();
  return user ? isSuperadminRole(user.role) : false;
}
